import React, { useState } from 'react';
import capitalize from 'lodash/capitalize';
import Lozenge from '@atlaskit/lozenge';
import { Flex, Inline, Box, Stack, Text } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import {
  getChargeElementV2,
  getLinkedEntitlementChargeElement,
  isStatusPageAddOnProduct,
  pluralize,
} from '@css-xp/ppc-utils';
import {
  EntitlementIcon,
  FormattedDate,
  OpenSidebarButton,
  SandboxLozenge,
  TrialLozenge,
  UpgradePremiumTrial,
  UpgradeAdvancedTrial,
} from '@css-xp/base';
import { NoData } from '@css-xp/ppc-common';
import { ROVO_PRODUCT_ID, STATUSPAGE_ADDON_PRODUCT_ID, STATUSPAGE_PRODUCT_ID } from '@css-xp/ppc-constants';
import { Currency } from '@css-xp/graphtypes/src/generated-types-new';
import InformationIcon from '@atlaskit/icon/core/information';
import { token } from '@atlaskit/tokens';
import { ProductStatusLozenge } from '../cart-line-items-table/product-status-lozenge';
import { CartProductSchema } from '../../types';
import { LinkedEntitlementRelationShipType, ProductStatus, ORDER_PAGE_SIDEBAR } from '../../../types';
import { CustomPanel } from './custom-panel';
import { getPlanChangeContent } from '../../utils/uof-utils/get-plan-change-content';
import { Estimate } from './estimate';
// TODO: Migrate the types. Re-enable and remove the 'any' usage in the file
// import { OrderSummaryLineItem, OrderSummaryLineItems } from './types';
import {
  entitlementInfoBoxStyles,
  cancelReasonDivStyles,
  InstalledAppsCustomPanelWrapper,
  ShowDetailsCustomPanelWrapper,
  EntitlementLozenge,
  ShowEstimateDetailsWrapper,
} from './line-items-custom-panel-v2.styles';

type NextBillDate = {
  // Same as that of type State in Cart
  cartLevel?: string;
  cloudSiteLevel?: Record<CartProductSchema['cloudSite'], string>;
  entitlementLevel?: Record<string, string>;
};

export type LineItemsCustomPanelV2Props = {
  lineItems: any; // OrderSummaryLineItems;
  nextBillDate: NextBillDate;
  isJiraFamily?: boolean;
  loading: boolean;
  showSandboxStatus?: boolean;
  selectedCurrency?: `${Currency}`;
  isTWCPhase2Enabled?: boolean;
  orgProductInstancesMap?: Record<string, CartProductSchema[]>;
};

const LineItem = ({
  lineItem,
  currency = Currency.USD,
  loading,
  nextBillDate,
  isOrgProductWithInstances,
  orgInstancesArr,
  isTWCPhase2Enabled,
}: {
  lineItem: any; // OrderSummaryLineItem;
  currency?: `${Currency}`;
  loading?: boolean;
  nextBillDate: NextBillDate;
  isOrgProductWithInstances?: boolean;
  orgInstancesArr?: CartProductSchema[];
  isTWCPhase2Enabled?: boolean;
}) => {
  const [isDetailsExpanded, setDetailExpanded] = useState(false);
  const showSandboxLozenge = lineItem?.isSandbox && lineItem?.productKey !== ROVO_PRODUCT_ID;
  const showTrialLozenge = !lineItem.productStatuses?.includes(ProductStatus.NEW) && lineItem.inTrial;
  const isAmendmentOrder = lineItem?.productStatuses?.some((productStatus: any) => {
    return productStatus === ProductStatus.EDITED;
  });

  const isTransferOrder = lineItem?.productStatuses?.some((productStatus: any) => {
    return productStatus === ProductStatus.TRANSFER;
  });

  const billingFrequency =
    // @ts-ignore
    lineItem?.isFreeProduct || !(lineItem?.billingCycleLabel || lineItem?.billingCycle)
      ? ''
      : // @ts-ignore
        ' | ' + capitalize(lineItem?.billingCycleLabel || lineItem?.billingCycle);
  const trialLength =
    lineItem?.inTrial && lineItem?.trialLengthInDays
      ? ` | ${pluralize(Number(lineItem?.trialLengthInDays), 'day')} trial`
      : '';
  const cartLevelCustomBillDate = nextBillDate.cartLevel;
  const cloudSiteLevelCustomBillDate = nextBillDate.cloudSiteLevel?.[lineItem.existingCloudSite ?? ''];

  const computedNextBillDate = cartLevelCustomBillDate || cloudSiteLevelCustomBillDate || lineItem?.nextBillDate;

  const cancelReason = lineItem?.productStatuses?.includes(ProductStatus.CANCEL) && lineItem?.cancelReason;
  const isAddOn = lineItem?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON;
  // @ts-ignore for linked entitlements we have productId as productKey
  const productId = lineItem?.productKey || lineItem?.productId;
  /**
   * we are converting productId = offeringId for statuspage addons and saving in store.
   * we are making this change because all statuspage addons have same product id
   * here is doc for more info https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/5047030716/Statuspage+addons+support+in+UOF
   */
  const isStatusPageAddOn = isStatusPageAddOnProduct(productId);
  const addOnChargeElement = getLinkedEntitlementChargeElement(isStatusPageAddOn ? STATUSPAGE_PRODUCT_ID : productId);
  const isApp = lineItem?.relationshipType === LinkedEntitlementRelationShipType.APP;

  return (
    <>
      <Box key={`${lineItem.id}`}>
        <Flex gap="space.025">
          <div className="line-item-entitlement-icon">
            <EntitlementIcon
              // this is required for statuspage addons to show correct icon
              productId={isStatusPageAddOn ? STATUSPAGE_ADDON_PRODUCT_ID : productId}
              className="avatar medium pt-4"
              isCloud
            />
          </div>
          <Stack xcss={entitlementInfoBoxStyles}>
            <Box>
              <Flex justifyContent="space-between" alignItems="center">
                <Box paddingBlockEnd={'space.150'}>
                  <Heading size="small">{lineItem?.name}</Heading>
                  <EntitlementLozenge className="entitlement-lozenge">
                    {showSandboxLozenge && lineItem?.isSandbox && lineItem.productKey !== ROVO_PRODUCT_ID && (
                      <div
                        className="line-item-product-in-trial"
                        data-testId={`line-item-product-sandbox-${lineItem?.id}`}
                      >
                        <SandboxLozenge />
                      </div>
                    )}
                    {showTrialLozenge && (
                      <div
                        className="line-item-product-in-trial"
                        data-testId={`line-item-product-in-trial-${lineItem?.id}`}
                      >
                        <TrialLozenge />
                      </div>
                    )}
                    {lineItem?.isOnUpgradeTrial && (
                      <div data-testId={`line-item-product-in-upgrade-trial-${lineItem?.id}`}>
                        {isApp ? <UpgradeAdvancedTrial /> : <UpgradePremiumTrial />}
                      </div>
                    )}
                    {lineItem?.otherLozengesOrStatuses?.map((status: string) => {
                      return (
                        <Lozenge key={`other-lozenges-${status}`} appearance={'new'}>
                          {capitalize(status)}
                        </Lozenge>
                      );
                    })}
                  </EntitlementLozenge>
                  {lineItem?.entitlementNumber ? (
                    <Text color="color.text.subtle" size="small">
                      {lineItem?.entitlementNumber}
                    </Text>
                  ) : null}
                  <Inline>
                    <Text color="color.text.subtle" size="small">
                      {lineItem?.offeringName} {billingFrequency} {trialLength}
                    </Text>
                  </Inline>
                  {isTWCPhase2Enabled && isOrgProductWithInstances && orgInstancesArr && orgInstancesArr?.length > 0 && (
                    <OpenSidebarButton
                      sidebar={ORDER_PAGE_SIDEBAR.INCLUDED_PRODUCTS}
                      sidebarOptions={{ sideBarDistanceFromTop: '0px' }}
                      payload={{
                        version: 2,
                        productName: lineItem?.name,
                        entitlementNumber: lineItem?.entitlementNumber,
                        includedProductsList: orgInstancesArr?.map((instance: CartProductSchema) => ({
                          productId: instance?.productId,
                          logoId: instance?.logoId,
                          productName: instance?.name,
                          cloudSiteHostname: instance?.cloudSite,
                          entitlementNumber: instance?.entitlementNumber,
                        })),
                      }}
                      spacing="compact"
                      testId="uof-order-summary-included-products-sidebar-button"
                      label="Included products"
                    >
                      {pluralize(orgInstancesArr.length, 'included product')}
                    </OpenSidebarButton>
                  )}
                </Box>
                <div>
                  {lineItem?.productStatuses?.map((status: any) => {
                    return <ProductStatusLozenge key={`product-status-${status}`} status={status} />;
                  })}
                </div>
              </Flex>
            </Box>
            {cancelReason && (
              <Flex xcss={cancelReasonDivStyles} gap="space.100" alignItems="center">
                <InformationIcon label="info-icon" color={token('color.icon.discovery')} />
                <Text color="color.text" size="medium" weight="semibold">
                  {cancelReason}
                </Text>
              </Flex>
            )}
            <ShowEstimateDetailsWrapper {...(cancelReason && { isDisabled: true })}>
              <Flex justifyContent="space-between">
                <div>Quantity Estimate</div>
                <div>
                  {/* @ts-ignore for apps user comes on activeUsers */}
                  {lineItem?.estimatedNumberOfUsers || lineItem.activeUsers}{' '}
                  {getChargeElementV2({
                    productId: lineItem?.productKey,
                    labelPlural: true,
                    ...(isAddOn && { chargeElement: addOnChargeElement, edition: lineItem?.offeringName }),
                  })}
                </div>
              </Flex>
              <Flex justifyContent="space-between">
                <div>List price</div>
                <div>
                  <Estimate currency={currency} price={lineItem?.listPrice ?? 0} loading={loading} />
                </div>
              </Flex>
              <Flex justifyContent="space-between">
                <div>Partner Discount {lineItem?.discountPercent}%</div>
                <div>
                  <Estimate currency={currency} price={-(lineItem?.totalDiscount ?? 0)} loading={loading} />
                </div>
              </Flex>

              {lineItem?.itemLevelTaxInfo?.map(([taxLabel, { tax, taxPercent }]: any) => {
                return (
                  <Flex key={`tax-(${taxPercent}-${taxLabel})`} justifyContent="space-between">
                    <div>
                      Tax {taxPercent}% {taxLabel}
                    </div>
                    <Estimate currency={currency} price={tax} loading={loading} />
                  </Flex>
                );
              })}
              {lineItem?.isAdminFeeApplicable && (
                <Flex justifyContent="space-between">
                  <div>Administration fee (DST)</div>
                  <div>
                    <Estimate currency={currency} price={lineItem?.adminFee ?? 0} loading={loading} />
                  </div>
                </Flex>
              )}
              <Flex justifyContent="space-between">
                <div>Subtotal estimate</div>
                <div>
                  <Estimate
                    currency={currency}
                    price={lineItem?.grandTotal ?? lineItem?.listPrice ?? 0}
                    loading={loading}
                  />
                </div>
              </Flex>
            </ShowEstimateDetailsWrapper>
            <ShowDetailsCustomPanelWrapper>
              <CustomPanel
                heading={isDetailsExpanded ? 'Hide Details' : 'Show Details'}
                isCustomPanelV2Enabled={true}
                onChange={() => setDetailExpanded(!isDetailsExpanded)}
                noHoverBackground={true}
              >
                <div className="entitlement-next-billdate">
                  Next bill date: {computedNextBillDate ? <FormattedDate date={computedNextBillDate} /> : <NoData />}
                </div>
                {lineItem.startDate && lineItem.renewalOrEndDate && (
                  <div className="entitlement-bill-period">
                    Billing period: {lineItem.startDate} - {lineItem.renewalOrEndDate}
                  </div>
                )}
                {isAmendmentOrder && (
                  <div className="entitlement-desclaimer">
                    {getPlanChangeContent({
                      initialOffering: lineItem?.initialOfferingName,
                      currentOffering: lineItem?.offeringName,
                      isInTrial: lineItem?.inTrial,
                      isItADowngrade: lineItem?.isItADowngrade,
                      isItAUpgrade: lineItem?.isItAUpgrade,
                      startDate: lineItem?.newPlanStartDate,
                      isATransferCase: isTransferOrder,
                    })}
                  </div>
                )}
              </CustomPanel>
            </ShowDetailsCustomPanelWrapper>
          </Stack>
        </Flex>
      </Box>
    </>
  );
};

export const LineItemsCustomPanelV2: React.FC<LineItemsCustomPanelV2Props> = ({
  lineItems,
  loading,
  nextBillDate,
  selectedCurrency = Currency.USD,
  isTWCPhase2Enabled = false,
  orgProductInstancesMap = {},
}) => {
  return (
    <>
      {lineItems.map((lineItem: any) => {
        const isOrgProductWithInstances = lineItem?.isOrgProduct && orgProductInstancesMap[lineItem.id]?.length > 0;
        const orgInstancesArr = orgProductInstancesMap[lineItem.id] || [];
        const hasAddOnsInTheLinkedList = lineItem?.appLineItems?.some(
          (appLineItem: any) => appLineItem?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON
        );
        const heading = (
          <Flex gap="space.100">
            <Text>{`Installed apps ${hasAddOnsInTheLinkedList ? 'and add-ons' : ''}`}</Text>
            <Box backgroundColor="color.background.neutral" paddingInline="space.050">
              {lineItem?.appLineItems?.length}
            </Box>
          </Flex>
        );
        return (
          <Box paddingInline="space.300" paddingBlock="space.200" key={lineItem.id}>
            <LineItem
              lineItem={lineItem}
              loading={loading}
              nextBillDate={nextBillDate}
              currency={selectedCurrency}
              isOrgProductWithInstances={isOrgProductWithInstances}
              orgInstancesArr={orgInstancesArr}
              isTWCPhase2Enabled={isTWCPhase2Enabled}
            />
            {lineItem?.appLineItems && lineItem?.appLineItems.length > 0 && (
              <InstalledAppsCustomPanelWrapper>
                <CustomPanel
                  heading={heading}
                  toggleButtonOnLeft={true}
                  noHoverBackground={true}
                  isCustomPanelV2Enabled={true}
                >
                  {lineItem?.appLineItems.map((lineItem: any) => {
                    return (
                      <Box paddingInline="space.0" paddingBlock="space.200" key={lineItem.id}>
                        <LineItem
                          // @ts-ignore
                          lineItem={lineItem}
                          loading={loading}
                          nextBillDate={nextBillDate}
                          currency={selectedCurrency}
                        />
                      </Box>
                    );
                  })}
                </CustomPanel>
              </InstalledAppsCustomPanelWrapper>
            )}
          </Box>
        );
      })}
    </>
  );
};
